const ENV_BY_LETTER = {
  d: "dev",
  q: "qa",
  u: "uat",
  p: "prod",
};

const ENV_BY_STAGE = {
  dev: "dev",
  development: "dev",
  qa: "qa",
  uat: "uat",
  staging: "uat",
  prod: "prod",
  production: "prod",
};

const ENV_LABEL_BY_TYPE = {
  dev: "Dev",
  qa: "QA",
  uat: "UAT",
  prod: "Prod",
};

const DOMAIN_LABEL_PATTERN = /^od([dqup])(\d+)-([1-9]\d{4})$/i;

function firstHostLabel(hostname) {
  const host = String(hostname || "").trim().toLowerCase();
  if (!host) return "";
  const withoutPort = host.split(":")[0];
  if (!withoutPort) return "";
  return withoutPort.split(".")[0] || "";
}

function normalizeHost(hostname) {
  return String(hostname || "").trim().toLowerCase().split(":")[0] || "";
}

function isLocalHost(hostname) {
  const host = normalizeHost(hostname);
  return host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost");
}

function readCookie(name) {
  if (typeof document === "undefined" || !document.cookie) return "";
  const prefix = `${name}=`;
  const match = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  if (!match) return "";
  return decodeURIComponent(match.slice(prefix.length));
}

function toPositiveInteger(value, fallback = 1) {
  const n = Number(value);
  if (Number.isInteger(n) && n > 0) return n;
  return fallback;
}

function buildEnvironmentDetails(environmentType, environmentNumber, customerNumber = null, source = "host") {
  if (!environmentType || !ENV_LABEL_BY_TYPE[environmentType]) return null;
  const number = toPositiveInteger(environmentNumber, 1);
  const environmentLabel = `${ENV_LABEL_BY_TYPE[environmentType]}(${number})`;
  return {
    environmentType,
    environmentNumber: number,
    customerNumber,
    environmentKey: `${environmentType}${number}`,
    environmentLabel,
    isProduction: environmentType === "prod",
    isNonProduction: environmentType !== "prod",
    source,
  };
}

export function parseEnvironmentFromHost(hostname) {
  const label = firstHostLabel(hostname);
  if (!label) return null;

  const match = DOMAIN_LABEL_PATTERN.exec(label);
  if (!match) return null;

  const environmentType = ENV_BY_LETTER[String(match[1] || "").toLowerCase()];
  const environmentNumber = Number(match[2]);
  const customerNumber = match[3];

  if (!environmentType || !Number.isInteger(environmentNumber) || environmentNumber <= 0 || !customerNumber) {
    return null;
  }

  return buildEnvironmentDetails(environmentType, environmentNumber, customerNumber, "host");
}

export function currentEnvironmentFromLocation() {
  if (typeof window === "undefined" || !window.location) return null;

  const hostParsed = parseEnvironmentFromHost(window.location.hostname);
  if (hostParsed) return hostParsed;

  const originalHost = readCookie("original_hostname");
  const originalHostParsed = parseEnvironmentFromHost(originalHost);
  if (originalHostParsed) {
    return {
      ...originalHostParsed,
      source: "original_hostname",
    };
  }

  if (!isLocalHost(window.location.hostname)) return null;

  const stageName = String(window.ENV?.STAGE_NAME || window.ENV?.ENVIRONMENT || "").trim().toLowerCase();
  const environmentType = ENV_BY_STAGE[stageName] || "dev";
  const environmentNumber = toPositiveInteger(window.ENV?.STAGE_NUMBER || window.ENV?.ENVIRONMENT_NUMBER, 1);
  return buildEnvironmentDetails(environmentType, environmentNumber, null, "localhost");
}

/**
 * True when the loaded admin/portal is running in the real production control plane
 * (odd-host `p`, localhost with stage production, or non-localhost with `window.ENV.STAGE_NAME` → prod).
 * Used to hide Production environment provisioning in lower (dev/qa/staging) deployments.
 * When stage cannot be determined, returns false (prefer hiding Production in admin).
 */
export function isPlatformContextProduction() {
  if (typeof window === "undefined") return false;

  const fromLoc = currentEnvironmentFromLocation();
  if (fromLoc) {
    return fromLoc.environmentType === "prod";
  }

  const stageName = String(
    window.ENV?.STAGE_NAME
      || window.ENV?.ENVIRONMENT
      || window.ENV?.API_GATEWAY_STAGE
      || ""
  )
    .trim()
    .toLowerCase();
  if (stageName) {
    return ENV_BY_STAGE[stageName] === "prod";
  }
  return false;
}
